<template>
  <div class="select" :class="`size-${size}`">
    <multiselect
        ref="select"
        v-on="listeners"
        v-bind="bind">
      <template slot="caret">
        <div class="multiselect__select">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M4.6098 5.83709L0.159755 1.29221C0.0567475 1.1871 -4.5756e-08 1.04677 -3.92158e-08 0.897152C-3.26756e-08 0.74753 0.0567475 0.607207 0.159755 0.50209L0.487394 0.167391C0.700889 -0.0503998 1.04788 -0.0503998 1.26105 0.167391L4.99793 3.98384L8.73895 0.163157C8.84196 0.0580392 8.97928 3.92497e-07 9.1257 3.98897e-07C9.27228 4.05304e-07 9.4096 0.0580392 9.51269 0.163157L9.84025 0.497855C9.94325 0.603056 10 0.743296 10 0.892918C10 1.04254 9.94325 1.18286 9.84025 1.28798L5.38613 5.83709C5.2828 5.94246 5.14484 6.00033 4.99817 6C4.85094 6.00033 4.71305 5.94246 4.6098 5.83709Z"
                fill="#AEC1D4"/>
          </svg>
        </div>
      </template>
      <template slot="tag" slot-scope="props">
        <span class="multiselect__tag group">
          <span>{{ props.option.title }}</span>
          <i aria-hidden="true"
             tabindex="1"
             class="hidden group-hover:block multiselect__tag-icon outline-none"
             @click.prevent="$refs.select.removeElement(props.option)"></i>
        </span>
      </template>
      <template slot="noOption">
        <slot name="noOption"></slot>
      </template>
      <template slot="singleLabel" slot-scope="props">
        <slot name="singleLabel" :option="props.option"></slot>
      </template>
      <template slot="option" slot-scope="props">
        <slot name="option" :option="props.option">
          <span v-if="props.option.$isLabel" class="group-label">{{ props.option.$groupLabel }}</span>
          <span v-else :class="{'disabled-option':props.option.$isDisabled}">{{ props.option[bind.label] }}</span>
        </slot>
      </template>
      <template slot="noOptions">
        <slot name="noOptions"></slot>
      </template>
      <template slot="noResult">
        <slot name="noResult">Ничего не найдено...</slot>
      </template>
    </multiselect>
    <label v-show="!!selectLabel"
           :class="{translateLabel}"
           @click="$refs.select.toggle()">
      {{ selectLabel }}
    </label>
    <slot name="errors"></slot>
  </div>
</template>

<script>
import IconRoundTick from "@/components/icons/IconRoundTick";

export default {
  name: "BaseSelect",
  components: {IconRoundTick},
  props: {
    selectLabel: {
      type: String,
      default: null
    },
    formattedOption: {
      type: Function,
      default: (val) => val
    },
    size: {
      type: String,
      validator: (val) => ['default', 'sm'].includes(val),
      default: 'default'
    }
  },
  data() {
    return {
      translateLabel: false,
      wasNoInput: true
    }
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        open: event => {
          this.translateLabel = true
          if (Object.keys(this.$listeners).includes('open')) this.$listeners['open']()
          this.translateLabel = true;
        },
        close: event => {
          if (Object.keys(this.$listeners).includes('close')) this.$listeners['close']()
          if (this.wasNoInput) {
            this.translateLabel = false
          }
        },
        input: event => {
          if (Object.keys(this.$listeners).includes('input')) this.$listeners['input'](event)
          this.translateLabel = !!event;
          this.wasNoInput = !event;
        }
      }
    },
    bind() {
      return {
        ...this.$attrs,
        ...this.$props,
        placeholder: '',
        showLabels: false
      }
    }
  },
  watch: {
    'bind.value': {
      handler(val) {
        if (val === null) {
          this.translateLabel = false
        }
        if (this.bind.taggable && this.bind.multiple) {
          this.translateLabel = !!this.bind.value.length
        } else {
          this.translateLabel = !!this.bind.value
        }
      }
    }
  },
  mounted() {
    if (this.bind.taggable && this.bind.multiple) {
      if (!!this.bind.value.length) this.translateLabel = true
    } else {
      if (!!this.bind.value) this.translateLabel = true
    }
  },
  beforeUpdate() {
    if (this.bind.taggable && this.bind.multiple) {
      if (!!this.bind.value.length) this.translateLabel = true
    } else {
      if (!!this.bind.value) this.translateLabel = true
    }
  }
}
</script>

<style scoped lang="scss">
.select {
  @apply relative;
  label {
    @apply text-md text-text-ternary px-5-6 top-0 pt-4-5 transform absolute ;
    transition: all .2s;

    &:before {
      @apply bg-transparent left-2;
      content: '';
      position: absolute;
      top: 35%;
      width: calc(100% - 1rem);
      height: 9px;
      filter: blur(3px);
      z-index: -1;
      transition: all .2s;
    }

    &.translateLabel {
      @apply text-xs text-text-label pt-0 pr-6 top-0 -translate-y-1/2;
      &:before {
        @apply bg-block;
      }
    }
  }

  .error {
    display: none;
  }

  &.--error {
    ::v-deep .multiselect {
      animation-name: shakeError;
      animation-fill-mode: forwards;
      animation-duration: 0.6s;
      animation-timing-function: ease-in-out;

      .multiselect__tags {
        @apply border-red;
      }
    }

    label {
      @apply text-xs text-red pt-0 pr-6 top-0 -translate-y-1/2 ;
      &:before {
        @apply bg-block;
      }
    }

    .error {
      @apply block text-red text-xs absolute bottom-0 transform translate-y-full px-5-6;
    }
  }

  ::v-deep .multiselect {

    .multiselect__single {
      @apply truncate;
    }

    .multiselect__select {
      @apply flex items-center justify-center;
      &:before {
        display: none;
      }
    }

    .multiselect__tags {
      @apply border border-border rounded-t text-xs text-text-secondary shadow-input bg-block;
      transition: all .2s;
    }

    .multiselect__tags-wrap {
      @apply inline-block;
      padding-top: .4rem;
      padding-bottom: .4rem;
      padding-left: .8rem;
    }

    .multiselect__tag {
      @apply py-1 px-3 inline-flex items-center bg-green relative overflow-visible;
      span {
        @apply relative block;
        z-index: 2;
      }

      &:before {
        content: '';
        @apply absolute top-0 bottom-0 m-auto bg-green rounded transform -rotate-45;
        right: -3px;
        height: 12px;
        width: 12px;
        z-index: 1;
      }
    }

    .multiselect__tag-icon {
      @apply relative leading-tight -my-1 -mr-1 ml-2 py-0 px-1 w-auto;
      height: 16px;
      z-index: 2;

      &:after {
        color: white;
      }

    }

    .multiselect__input, .multiselect__single {
      @apply text-md cursor-pointer leading-10 bg-transparent;
      padding-left: .8rem;
      margin-bottom: 0;
    }

    .multiselect__content-wrapper {
      @apply border-border z-100 overflow-x-hidden;
    }

    .multiselect__content {
      @apply max-w-full;
    }

    .multiselect__option {
      @apply flex items-center
      text-md text-text leading-relaxed
      bg-block pl-6 pr-8 py-2 truncate;
      &:after {
        white-space: nowrap;
        line-height: 40px;
        background: #5897fb;
        font-size: 11px;
        color: #fff;
      }

      span {
        @apply truncate;
      }

      &--group {
        @apply p-0;
        background: var(--bg-primary-block) !important;
        color: var(--text-text-muted) !important;
        font-size: 1.1rem;
        line-height: 2.5rem;
        min-height: 0;
        height: auto;

        .group-label {
          @apply w-full border-b border-border-hover p-0 px-4;
        }
      }

      &--disabled {
        pointer-events: none;
      }

      &.multiselect__option--selected {
        @apply font-bold relative;
        &:after {
          content: '';
          @apply w-4 h-4 absolute p-0;
          background: url("./round-tick.svg");
          background-size: cover;
          top: 50%;
          right: 1rem;
          transform: translateY(-50%);
        }
      }

      &.multiselect__option--highlight,
      &.multiselect__option--selected.multiselect__option--highlight {
        @apply bg-highlight;
      }

    }

    .multiselect__option {
      &--group,
      &--disabled {
        @apply bg-border-hover text-text-label;

        .disabled-option {
          @apply w-full text-md text-text-ternary leading-relaxed
          bg-highlight px-6 py-4 truncate cursor-not-allowed;
        }
      }
    }

    &.multiselect--above {

    }

    &.multiselect--active {
      z-index: 20;

      + label {
        z-index: 30;
      }

      &.multiselect--above .multiselect__tags {
        @apply rounded-t-none;
      }
    }

    &.multiselect--active, &:hover {
      .multiselect__tags {
        @apply rounded-t border-border-hover;
      }

      .multiselect__content-wrapper {
        @apply border-border-hover;
      }
    }

    &--disabled {
      background: transparent;

      .multiselect__select {
        background: transparent;
      }
    }
  }

  &.size-sm {
    ::v-deep .multiselect {
      min-height: 25px;

      &__select {
        width: 25px;
        height: 25px;
      }

      &__tags {
        min-height: 25px;
        padding: 0 25px 0 0;
      }

      &__single {
        @apply text-2sm text-text-label;
      }

      &__option {
        @apply text-2sm;
        padding: 0 25px 0 8px;
        min-height: 25px;
      }
    }
  }

  &:hover {
    ::v-deep .multiselect__tags {
      @apply border-border-hover;
    }
  }
}
</style>